"use client";

import { useEffect, useState } from "react";
import { Share2, Link2, Check, MessageCircle } from "lucide-react";

/**
 * Lightweight share row for blog posts. No third-party scripts — uses the
 * native Web Share API where available, with copy-link + WhatsApp + email
 * fallbacks. `url` is the absolute canonical URL of the post.
 */
export function ShareButtons({ url, title }: { url: string; title: string }) {
  const [copied, setCopied] = useState(false);

  /**
   * Feature-detect AFTER mount — never during render.
   *
   * `typeof navigator !== "undefined" && typeof navigator.share === "function"`
   * evaluated in the render body stops the Worker crashing, but it does NOT
   * make hydration stable: on the server there is no `navigator`, so the Share
   * button is ABSENT from the SSR HTML, while the first client render on iOS
   * Safari / Android Chrome / Edge finds `navigator.share` and renders it. That
   * extra ELEMENT is a hydration mismatch — React #418 with `args[]=HTML` (the
   * exact error seen in /admin/errors), and only ever on mobile, which is why
   * it looked random.
   *
   * The mounted gate makes the first client render match the server (no
   * button), then reveals it on the post-hydrate re-render. Same pattern the
   * cart badge uses for persisted-store values.
  */
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- deliberately reveals browser-only Web Share support only after hydration.
    setMounted(true);
  }, []);

  const canNativeShare = mounted && typeof navigator.share === "function";

  const nativeShare = async () => {
    try {
      await navigator.share({ title, url });
    } catch {
      /* user cancelled or unsupported — ignore */
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard blocked — ignore */
    }
  };

  const waHref = `https://wa.me/?text=${encodeURIComponent(`${title} ${url}`)}`;
  const mailHref = `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(`I thought you'd like this: ${url}`)}`;

  const cls =
    "inline-flex min-h-11 items-center gap-2 rounded-full border border-border bg-background px-4 text-sm font-medium text-muted-foreground transition-colors hover:border-primary/50 hover:text-foreground";

  return (
    <div className="flex flex-wrap items-center gap-2.5">
      <span className="text-sm font-medium text-foreground">Share</span>
      {canNativeShare && (
        <button type="button" onClick={nativeShare} className={cls} aria-label="Share this guide">
          <Share2 className="h-4 w-4" aria-hidden="true" /> Share
        </button>
      )}
      <button type="button" onClick={copy} className={cls} aria-label="Copy link to this guide">
        {copied ? <Check className="h-4 w-4 text-primary" aria-hidden="true" /> : <Link2 className="h-4 w-4" aria-hidden="true" />}
        {copied ? "Copied" : "Copy link"}
      </button>
      <a href={waHref} target="_blank" rel="noopener noreferrer" className={cls}>
        <MessageCircle className="h-4 w-4" aria-hidden="true" /> WhatsApp
        <span className="sr-only"> (opens in new tab)</span>
      </a>
      <a href={mailHref} className={cls}>
        Email
      </a>
    </div>
  );
}
